import { useLanguage } from '@/hooks/useLanguage';
import { tx as translateInterfaceText } from '@/utils/i18n';
import React from 'react';
import { VideoFeatures } from '@/store/useVideoFeatures';
import { Play, RotateCcw } from 'lucide-react';
import './continue-watching-animations.css';

interface ContinueWatchingOverlayProps {
  features: VideoFeatures;
  onContinue: () => void;
  onRestart: () => void;
  onCancel?: () => void;
}

export const ContinueWatchingOverlay: React.FC<ContinueWatchingOverlayProps> = ({
  features,
  onContinue,
  onRestart,
  onCancel,
}) => {
  useLanguage();
  const overlay = features.continueWatchingOverlay || {};

  // Textos com fallbacks (igual ao projeto antigo)
  const title = overlay.message || translateInterfaceText("Você já começou a assistir esse vídeo");
  const continueText = overlay.continueText || 'Continuar assistindo?';
  const restartText = overlay.restartText || 'Assistir do início';

  // Cores
  const backgroundColor = overlay.backgroundColor || '#2563EB';
  const textColor = overlay.textColor || '#FFFFFF';
  const playButtonBgColor = overlay.playButtonBgColor || 'transparent';
  const restartButtonBgColor = overlay.restartButtonBgColor || 'transparent';

  // Template type - com mapeamento dos IDs antigos para os novos
  const templateFromOverlay = overlay.template || 'continue';
  // Mapear IDs antigos para novos
  const templateMap: Record<string, string> = {
    'template1': 'continue',      // Template antigo 1 → Template padrão
    'template2': 'continue',      // Template antigo 2 → Template padrão
    'template3': 'continue',      // Template antigo 3 → Template padrão
    'continue': 'continue',       // Já é o novo
    'continueSmall': 'continueSmall', // Já é o novo
    'custom': 'continue'          // Custom usa o padrão
  };
  const template = templateMap[templateFromOverlay] || 'continue';

  // Template padrão (fullscreen com botões circulares)
  if (template === 'continue' || !template || template === 'custom') {

    return (
      <div
        className="absolute inset-0 z-[9999] flex flex-col items-center justify-center animate-continue-watching-enter"
        style={{ backgroundColor }}
      >
        {/* Título */}
        <h6
          className="font-semibold text-center mb-6 px-4"
          style={{
            color: textColor,
            fontSize: '1.5rem'
          }}
        >
          {title}
        </h6>

        {/* Área dos botões */}
        <div className="flex items-center justify-center gap-8">
          {/* Botão Continuar */}
          <button
            onClick={onContinue}
            className="flex flex-col items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <div
              className="h-14 w-14 rounded-full flex items-center justify-center"
              style={{ border: `2px solid ${textColor}`, backgroundColor: playButtonBgColor }}
            >
              <Play size={24} color={textColor} strokeWidth={2} fill={textColor} style={{ marginLeft: '2px' }} />
            </div>
            <span
              className="text-sm font-normal"
              style={{ color: textColor }}
            >
              {continueText}
            </span>
          </button>

          {/* Botão Reiniciar */}
          <button
            onClick={onRestart}
            className="flex flex-col items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <div
              className="h-14 w-14 rounded-full flex items-center justify-center"
              style={{ border: `2px solid ${textColor}`, backgroundColor: restartButtonBgColor }}
            >
              <RotateCcw size={24} color={textColor} strokeWidth={2} />
            </div>
            <span
              className="text-sm font-normal"
              style={{ color: textColor }}
            >
              {restartText}
            </span>
          </button>
        </div>
      </div>
    );
  }

  // Template compacto (modal centralizado com botões retangulares)
  if (template === 'continueSmall') {
    return (
      <div className="absolute inset-0 z-[777] flex items-center justify-center overflow-hidden">
        <div
          className="flex flex-col items-center justify-center rounded-lg animate-continue-watching-enter gap-[clamp(10px,3%,18px)] py-[clamp(14px,5%,26px)] px-[clamp(12px,5%,26px)]"
          style={{
            backgroundColor,
            width: 'clamp(150px, 88%, 380px)',
            maxWidth: '94%',
          }}
        >
          {/* Título */}
          <h6
            className="font-semibold text-center leading-tight"
            style={{
              color: textColor,
              fontSize: 'clamp(0.55rem, 2.2cqw, 0.85rem)',
            }}
          >
            {title}
          </h6>

          {/* Área dos botões */}
          <div className="w-full flex items-center justify-center flex-wrap gap-[clamp(10px,4%,20px)]">
            {/* Botão Continuar */}
            <button
              onClick={onContinue}
              className="flex items-center justify-center gap-1 rounded-md cursor-pointer hover:opacity-80 transition-opacity"
              style={{
                backgroundColor: '#585858',
                color: textColor,
                padding: 'clamp(6px, 2%, 12px) clamp(10px, 3.5%, 18px)',
                flex: 1,
              }}
            >
              <Play
                color={textColor}
                strokeWidth={2}
                fill={textColor}
                style={{ width: 'clamp(10px, 3%, 16px)', height: 'clamp(10px, 3%, 16px)', marginLeft: '1px', flexShrink: 0 }}
              />
              <span
                className="font-normal whitespace-nowrap"
                style={{ color: textColor, fontSize: 'clamp(0.5rem, 2cqw, 0.75rem)' }}
              >
                {continueText}
              </span>
            </button>

            {/* Botão Reiniciar */}
            <button
              onClick={onRestart}
              className="flex items-center justify-center gap-1 rounded-md cursor-pointer hover:opacity-80 transition-opacity"
              style={{
                backgroundColor: '#007BFF',
                color: textColor,
                padding: 'clamp(6px, 2%, 12px) clamp(10px, 3.5%, 18px)',
                flex: 1,
              }}
            >
              <RotateCcw
                color={textColor}
                strokeWidth={2}
                style={{ width: 'clamp(10px, 3%, 16px)', height: 'clamp(10px, 3%, 16px)', flexShrink: 0 }}
              />
              <span
                className="font-normal whitespace-nowrap"
                style={{ color: textColor, fontSize: 'clamp(0.5rem, 2cqw, 0.75rem)' }}
              >
                {restartText}
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
